import React, { useRef, useState } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useSystem } from '@ohif/core';
import { Icons } from '@ohif/ui-next';
import { IMPORT_STUDY_ACCEPT, VIEWER_HEADER_ACTION_BUTTON_CLASSNAME } from './constants';
import { getImportStudyConfig } from './importStudyConfig';
import { handleImportStudyFiles } from './importStudyHandler';
import { createStudyDisplaySetRequester } from './createStudyDisplaySetRequester';
import { loadImportedStudyIntoViewer } from './loadImportedStudyIntoViewer';
import { IMPORT_STUDY_MESSAGES } from './importStudyMessages';
import { getImportStudyPhaseLabel, type ImportStudyPhase } from './importStudyPhases';
import { switchImportedStudy } from './switchImportedStudy';
import { useImportedStudyStore } from '../../stores/useImportedStudyStore';
import ImportStudyUploadIcon from './ImportStudyUploadIcon';

function ImportStudyButton() {
  const { t } = useTranslation('PatientView');
  const { servicesManager, extensionManager, commandsManager } = useSystem();
  const { uiNotificationService, displaySetService } = servicesManager.services;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [processingPhase, setProcessingPhase] = useState<ImportStudyPhase | null>(null);

  const isProcessing = processingPhase !== null;
  const processingLabel = processingPhase ? getImportStudyPhaseLabel(processingPhase) : '';

  const showImportError = (message: string) => {
    uiNotificationService.show({
      title: t('ImportStudy'),
      message,
      type: 'error',
      duration: 6000,
    });
  };

  const openFilePicker = () => {
    if (!isProcessing) {
      fileInputRef.current?.click();
    }
  };

  const onFileInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;

    if (!files?.length) {
      return;
    }

    setProcessingPhase('uploading');

    try {
      const result = await handleImportStudyFiles(Array.from(files), {
        config: getImportStudyConfig(extensionManager._appConfig),
        onPhaseChange: setProcessingPhase,
        onValidationError: showImportError,
      });

      if (!result.valid) {
        return;
      }

      setProcessingPhase('loading_viewer');

      const studyInstanceUIDs = await loadImportedStudyIntoViewer(
        result.parsedInstances,
        extensionManager
      );

      const activeStudyInstanceUID =
        useImportedStudyStore.getState().activeStudyInstanceUID ?? studyInstanceUIDs[0];

      if (activeStudyInstanceUID) {
        const activeDataSource = extensionManager.getActiveDataSource()[0];
        const requestDisplaySetCreationForStudy = createStudyDisplaySetRequester(
          extensionManager,
          activeDataSource
        );

        await switchImportedStudy(activeStudyInstanceUID, {
          servicesManager,
          commandsManager,
          displaySetService,
          requestDisplaySetCreationForStudy,
        });
      }

      uiNotificationService.show({
        title: t('ImportStudy'),
        message: IMPORT_STUDY_MESSAGES.SUCCESS,
        type: 'success',
        duration: 4000,
      });
    } catch (error) {
      console.error('[ImportStudy] Failed to load imported study into viewer:', error);
      showImportError(IMPORT_STUDY_MESSAGES.LOAD_FAILED);
    } finally {
      setProcessingPhase(null);
      event.target.value = '';
    }
  };

  return (
    <div
      className="relative flex items-center"
      data-cy="import-study-control"
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={IMPORT_STUDY_ACCEPT}
        multiple
        className="hidden"
        disabled={isProcessing}
        data-cy="import-study-file-input"
        onChange={onFileInputChange}
      />
      <button
        type="button"
        disabled={isProcessing}
        aria-busy={isProcessing}
        aria-live="polite"
        aria-label={isProcessing ? processingLabel : t('ImportStudy')}
        title={isProcessing ? processingLabel : t('ImportStudy')}
        className={classnames(
          VIEWER_HEADER_ACTION_BUTTON_CLASSNAME,
          'viewer-ris-header-action-btn disabled:cursor-not-allowed disabled:opacity-70',
          isProcessing ? 'min-w-0 sm:min-w-[9.5rem]' : 'min-w-0 sm:min-w-[6.5rem]'
        )}
        onClick={openFilePicker}
        data-cy="import-study-button"
      >
        {isProcessing ? (
          <>
            <Icons.LoadingSpinner className="h-4 w-4 shrink-0 text-white" aria-hidden />
            <span className="viewer-ris-header-action-label">{processingLabel}</span>
          </>
        ) : (
          <>
            <ImportStudyUploadIcon className="h-4 w-4 shrink-0 text-white" />
            <span className="viewer-ris-header-action-label">{t('ImportStudy')}</span>
          </>
        )}
      </button>
    </div>
  );
}

export default ImportStudyButton;
